import axios from "axios";
import { buildPayload, validateBooking, ERRORS, REQUEST_TIMEOUT_MS } from "./contract";
import { APPS_SCRIPT_URL, APPS_SCRIPT_SECRET } from "./config";

/**
 * =============================================================================
 * ADAPTER — Google Apps Script Web App
 * =============================================================================
 *
 * Implements the contract in ./contract.js. See ./index.js for how one adapter
 * is selected.
 *
 * -----------------------------------------------------------------------------
 * 🔴 DO NOT CHANGE THE CONTENT-TYPE TO application/json
 * -----------------------------------------------------------------------------
 * The header below is `text/plain;charset=utf-8` ON PURPOSE, and it will look
 * wrong to anyone who notices that the body is JSON. It is not wrong. Changing
 * it breaks the integration completely, and the failure is a CORS error in the
 * browser console that says nothing about content types.
 *
 * Why:
 *
 *   A cross-origin POST only skips the CORS preflight when it qualifies as a
 *   "simple request". One condition is that Content-Type is one of exactly
 *   three values:
 *       application/x-www-form-urlencoded
 *       multipart/form-data
 *       text/plain
 *
 *   `application/json` is NOT on that list, so sending it makes the browser fire
 *   an OPTIONS preflight first.
 *
 *   Apps Script Web Apps cannot answer a preflight. A published script exposes
 *   doGet and doPost and nothing else — there is no doOptions, and no way to
 *   attach the Access-Control-Allow-* headers the preflight needs. The OPTIONS
 *   request fails, and the browser never sends the POST at all.
 *
 *   With `text/plain;charset=utf-8` the request is "simple", no preflight is
 *   fired, and the POST goes straight through.
 *
 * The BODY IS STILL JSON. We stringify it here and the Apps Script parses it
 * server-side with JSON.parse(e.postData.contents). The header describes what
 * the browser is allowed to send without asking permission first; it is not a
 * claim about the payload's structure.
 *
 * -----------------------------------------------------------------------------
 * WHY THIS RESOLVES OPTIMISTICALLY
 * -----------------------------------------------------------------------------
 * An Apps Script Web App does not reply directly. It answers with a 302 to
 * script.googleusercontent.com, and THAT response carries no CORS headers — so
 * the browser refuses to let JavaScript read it. In practice the request either
 * completes (we cannot see the body) or fails outright (we can see that).
 *
 * So a completed request is treated as success. It is genuinely fire-and-forget:
 * the notification email the script sends is the real confirmation, not this
 * return value. `id` is deliberately absent — inventing one would imply a
 * backend reference we never received.
 *
 * ./index.js explains why the FastAPI adapter normalises DOWN to match this
 * rather than the reverse.
 */

/**
 * @param   {import("./contract").BookingSubmission} data
 * @returns {Promise<import("./contract").BookingResult>}
 */
export async function submitBooking(data) {
  if (!APPS_SCRIPT_URL) {
    return { ok: false, error: ERRORS.NOT_CONFIGURED };
  }

  // ---------------------------------------------------------------------------
  // 🔴 REFUSE TO SEND WITHOUT A SECRET — and this guard matters more than it looks
  // ---------------------------------------------------------------------------
  // The backend fails closed: no secret, or a wrong one, and it returns
  // {ok:false, error:"unauthorized"} without writing anything.
  //
  // But this adapter CANNOT SEE THAT. Apps Script answers with a 302 to
  // script.googleusercontent.com carrying no CORS headers, so the browser will
  // not let us read the body — and a rejection still arrives as a completed
  // HTTP request. Without this guard the sequence would be:
  //
  //     empty secret → request completes → adapter resolves { ok: true }
  //                  → visitor sees the success panel
  //                  → backend wrote NOTHING, sent NOTHING
  //
  // Silent data loss, presented to the visitor as success. That is the worst
  // failure mode in this whole system, and a misconfigured build would produce
  // it on every submission with nothing anywhere to say so.
  //
  // Checking client-side turns it into a loud, immediate failure instead: the
  // visitor gets the error toast and the phone number, and `not_configured`
  // appears in the console. A broken build looks broken.
  // ---------------------------------------------------------------------------
  if (!APPS_SCRIPT_SECRET) {
    return { ok: false, error: ERRORS.NOT_CONFIGURED };
  }

  // buildPayload() returns a fixed seven-key literal, so anything not in the
  // contract — including the honeypot — is dropped by it. Both extras are added
  // HERE, after it, rather than by widening the contract: they are transport
  // concerns only this backend knows about, and the FastAPI adapter neither
  // sends nor understands them.
  const payload = {
    ...buildPayload(data),
    secret: APPS_SCRIPT_SECRET,
    // Honeypot. Empty for a real visitor; a bot's autofill gives it away.
    // The backend silently accepts and discards anything with this set.
    company: typeof data.company === "string" ? data.company : "",
  };

  const { valid, missing } = validateBooking(payload);
  if (!valid) {
    return { ok: false, error: `${ERRORS.INVALID}: ${missing.join(",")}` };
  }

  try {
    await axios.post(APPS_SCRIPT_URL, JSON.stringify(payload), {
      // See the block comment above before touching this line.
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      timeout: REQUEST_TIMEOUT_MS,
      // The redirect chain to script.googleusercontent.com is opaque to us. We
      // never read the body, so there is nothing to parse and nothing to check.
      transformResponse: [(raw) => raw],
    });

    // Optimistic. No `id` — we did not receive one.
    return { ok: true };
  } catch (err) {
    return { ok: false, error: classify(err) };
  }
}

/** Map an axios rejection onto a contract error code. */
function classify(err) {
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return ERRORS.TIMEOUT;
  }
  if (err?.response) {
    return `${ERRORS.SERVER}:${err.response.status}`;
  }
  // No response object: DNS failure, refused connection, offline, or the CORS
  // block you would get if someone changed the Content-Type above.
  return ERRORS.NETWORK;
}
